import { useQuery } from "@tanstack/react-query";
import { fetchTenantUiSettings } from "../services/tenantUi/tenantUiApi";
import { queryKeys } from "../services/queryKeys";
import type { ApiVersion } from "../services/api";

const STALE_TIME_MS = 5 * 60 * 1000;

/** The fallback when the settings can't be read and the backend gave no reason. */
export const ARCHITECTURE_UNREADABLE = "Magic couldn't read your organization's settings.";

export type ArchitectureState =
  | { status: "loading" }
  | { status: "error"; error: unknown; retry: () => void }
  | { status: "ready"; oldArchitecture: boolean };

/**
 * Which backend the tenant runs: `old_architecture` from the tenant's UI settings.
 * Never guesses while loading or on an error: the caller shows a loading or error state.
 */
export const useArchitecture = (): ArchitectureState => {
  const query = useQuery({
    queryKey: queryKeys.tenantUi.settings,
    queryFn: fetchTenantUiSettings,
    staleTime: STALE_TIME_MS,
  });
  if (query.isSuccess) return { status: "ready", oldArchitecture: query.data.oldArchitecture };
  if (query.isError) {
    return { status: "error", error: query.error, retry: () => void query.refetch() };
  }
  return { status: "loading" };
};

export interface StoryCreationPath {
  /** The tenant's architecture is known. Make nothing before: `version` is only a placeholder. */
  isReady: boolean;
  /** Stories are made through api-service: the tenant runs the new architecture. */
  newPath: boolean;
  version: ApiVersion;
  /** Why the architecture couldn't be read; `undefined` while loading or once known. */
  error: unknown;
  retry: () => void;
}

const noRetry = () => undefined;

/** The path a new Story is made on, from `useArchitecture`. */
export const useStoryCreationPath = (): StoryCreationPath => {
  const architecture = useArchitecture();
  const isReady = architecture.status === "ready";
  const newPath = isReady && !architecture.oldArchitecture;
  return {
    isReady,
    newPath,
    version: newPath ? "v2" : "v1",
    error: architecture.status === "error" ? architecture.error : undefined,
    retry: architecture.status === "error" ? architecture.retry : noRetry,
  };
};
